/**
 * Bruno → Export → "Bruno Collection" (one JSON file) → IR.
 *
 * That file is Bruno's in-memory model with uids stripped
 * (`bruno-app/src/utils/collections/export.js`):
 *   { name, version, items[], environments[], root:{request,docs}, brunoConfig, exportedUsing:'Bruno/x' }
 * items: `{type:'http'|'graphql'|'grpc'|'ws'|'folder', name, seq, request, items, root}`;
 * secrets in `environments` are already blanked by Bruno's exporter.
 */
import { authFromBrunoModel, emptyDefaults, emptyCounts, row, valueToString } from './ir';

const arr = (v) => (Array.isArray(v) ? v : []);
const str = (v) => (v == null ? '' : String(v));

const kvRows = (items) =>
  arr(items)
    .filter((h) => h && h.name != null)
    .map((h) => row(h.name, h.value, h.enabled !== false));

const seqOf = (v) => (Number.isFinite(Number(v)) && v !== '' && v != null ? Number(v) : undefined);

function readBody(body) {
  if (!body || typeof body !== 'object') return { mode: 'none' };
  switch (str(body.mode)) {
    case 'json':
      return { mode: 'json', text: str(body.json) };
    case 'text':
      return { mode: 'text', text: str(body.text) };
    case 'xml':
      return { mode: 'xml', text: str(body.xml) };
    case 'sparql':
      return { mode: 'sparql', text: str(body.sparql) };
    case 'formUrlEncoded':
      return { mode: 'urlencoded', rows: kvRows(body.formUrlEncoded) };
    case 'multipartForm':
      return {
        mode: 'multipart',
        rows: arr(body.multipartForm)
          .filter((f) => f && f.name != null)
          .map((f) => {
            const enabled = f.enabled !== false;
            if (f.type === 'file') {
              const paths = Array.isArray(f.value) ? f.value : [f.value];
              return { key: str(f.name), value: '', enabled, type: 'file', src: paths.filter(Boolean).map(String).join('|') };
            }
            return { key: str(f.name), value: valueToString(f.value), enabled, type: 'text' };
          }),
      };
    case 'graphql':
      return { mode: 'graphql', graphql: { query: str(body.graphql?.query), variables: str(body.graphql?.variables) } };
    case 'file':
      return { mode: 'file' };
    default:
      return { mode: 'none' };
  }
}

function readScripts(request) {
  return {
    pre: str(request?.script?.req).trim(),
    post: str(request?.script?.res).trim(),
    tests: str(request?.tests).trim(),
  };
}

function readDefaults(request) {
  const d = emptyDefaults();
  if (!request || typeof request !== 'object') return d;
  d.headers = kvRows(request.headers);
  if (request.auth && typeof request.auth === 'object') d.auth = authFromBrunoModel(request.auth);
  d.vars = kvRows(request.vars?.req);
  d.scripts = readScripts(request);
  return d;
}

function itemToNode(item) {
  const type = str(item.type).replace(/-request$/, '');
  if (type === 'folder') {
    return {
      kind: 'folder',
      name: str(item.name || item.root?.meta?.name) || 'Folder',
      seq: seqOf(item.seq ?? item.root?.meta?.seq),
      docs: str(item.root?.docs),
      defaults: readDefaults(item.root?.request),
      children: arr(item.items).filter((i) => i && typeof i === 'object').map(itemToNode),
    };
  }
  const name = str(item.name) || 'Untitled Request';
  const req = item.request;
  if ((type !== 'http' && type !== 'graphql') || !req || typeof req !== 'object') {
    return { kind: 'request', name, seq: seqOf(item.seq), reqType: 'unsupported', typeName: type || 'unknown' };
  }
  const params = arr(req.params);
  return {
    kind: 'request',
    name,
    seq: seqOf(item.seq),
    reqType: type,
    method: str(req.method || 'GET').toUpperCase(),
    url: str(req.url),
    headers: kvRows(req.headers),
    query: kvRows(params.filter((p) => p && (p.type || 'query') === 'query')),
    pathParams: kvRows(params.filter((p) => p && p.type === 'path')),
    body: readBody(req.body),
    // A request's `auth` is absent only in hand-made files; Bruno always writes a mode.
    auth: authFromBrunoModel(req.auth || { mode: 'inherit' }),
    scripts: readScripts(req),
    docs: str(req.docs),
    counts: {
      ...emptyCounts(),
      vars: arr(req.vars?.req).length + arr(req.vars?.res).length,
      assertions: arr(req.assertions).length,
      examples: arr(item.examples).length,
    },
  };
}

export function brunoJsonToIR(data) {
  return {
    name: str(data.name) || 'Bruno Collection',
    docs: str(data.root?.docs),
    defaults: readDefaults(data.root?.request),
    children: arr(data.items).filter((i) => i && typeof i === 'object').map(itemToNode),
    environments: arr(data.environments).map((env) => ({
      name: str(env?.name) || 'Environment',
      variables: arr(env?.variables)
        .filter((v) => v && v.name != null && String(v.name) !== '')
        .map((v) => ({
          key: String(v.name),
          value: v.secret === true ? '' : valueToString(v.value),
          enabled: v.enabled !== false,
          secret: v.secret === true,
        })),
    })),
  };
}
